// Meme Settings — the enriched Imgflip template library management UI.
// Lives in the Admin Dashboard (Content & Brands → Meme Settings); the shared
// SORT_TABS / rankOf helpers are also consumed by the Meme Generator picker.
import { useState, useMemo, useEffect, useRef, type ReactNode } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Sparkles, Loader2, Check, AlertTriangle, RefreshCw,
  X, ImageIcon, Search, Settings2, ChevronDown, Eye, Type, LayoutList, Smile, Globe, Pencil, Trash2, Star,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

export type LibraryTemplate = {
  id: string;
  templateId: string;
  name: string;
  url: string;
  boxCount: number | null;
  visual: string | null;
  textPlacement: string | null;
  boxMeaning: string | null;
  whyFunny: string | null;
  generality: string | null;
  enriched: boolean;
  lastEnrichError?: string | null;
  lastEnrichErrorAt?: string | null;
  imageDeadAt?: string | null;
  rankTopNew?: number | null;
  rankTop30?: number | null;
  rankTopAll?: number | null;
  // Member of the curated, global "Recommended" list.
  recommended?: boolean;
};

// The three Imgflip sorts a template can rank in. "Top New" is the default
// picker tab (matches the tool's original top-new-only behavior).
export const SORT_TABS = [
  { key: "top-new", label: "Top New", rankKey: "rankTopNew" },
  { key: "top-30-days", label: "Top 30 Days", rankKey: "rankTop30" },
  { key: "top-all-time", label: "Top All Time", rankKey: "rankTopAll" },
] as const;
export type SortTabKey = (typeof SORT_TABS)[number]["key"];
export type RankKey = (typeof SORT_TABS)[number]["rankKey"];

export function rankOf(t: { rankTopNew?: number | null; rankTop30?: number | null; rankTopAll?: number | null }, key: RankKey): number | null {
  return (t[key] ?? null) as number | null;
}

type AutomationRunInfo = {
  lastAt: string | null;
  lastResult: Record<string, any> | null;
};

type ImgflipPremiumStatus = {
  configured: boolean;
  state: "unconfigured" | "untested" | "active" | "invalid_credentials" | "error";
  lastOkAt: string | null;
  lastErrorAt: string | null;
  lastError: string | null;
};

type SeedStatus = {
  running: boolean;
  trigger?: "manual" | "auto" | null;
  mode?: "new" | "all" | null;
  total: number;
  skipped: number;
  enriched: number;
  failed: number;
  deadSkipped?: number;
  batchSize?: number;
  remaining?: number;
  startedAt?: string | null;
  finishedAt?: string | null;
  error: string | null;
  imgflipPremium?: ImgflipPremiumStatus;
  automation?: {
    refresh: AutomationRunInfo;
    enrich: AutomationRunInfo;
  };
};

// Master-only summary of the paid Imgflip Premium search path. The picker's
// "Search Imgflip" tab always works either way (Premium first, free scrape
// fallback) — this line exists so a broken paid path is never invisible.
function premiumSearchLabel(s: ImgflipPremiumStatus): string {
  if (!s.configured) return "not configured — using the free Imgflip scrape (add IMGFLIP_USERNAME / IMGFLIP_PASSWORD to enable)";
  switch (s.state) {
    case "active":
      return `active${s.lastOkAt ? ` — last successful search ${new Date(s.lastOkAt).toLocaleString()}` : ""}`;
    case "invalid_credentials":
      return `credentials rejected — check IMGFLIP_USERNAME / IMGFLIP_PASSWORD and the Premium subscription; searches are falling back to the free scrape${s.lastError ? ` (${s.lastError.slice(0, 120)})` : ""}`;
    case "error":
      return `falling back to the free scrape — last Premium error${s.lastErrorAt ? ` ${new Date(s.lastErrorAt).toLocaleString()}` : ""}${s.lastError ? `: ${s.lastError.slice(0, 120)}` : ""}`;
    default:
      return "configured — no searches yet this session";
  }
}

// ── Meme Settings: enriched template library ─────────────────────────────────

const CONTEXT_FIELDS: Array<{ key: keyof LibraryTemplate; label: string; icon: ReactNode }> = [
  { key: "visual", label: "What's happening visually", icon: <Eye className="h-3.5 w-3.5" /> },
  { key: "textPlacement", label: "Where the text goes", icon: <Type className="h-3.5 w-3.5" /> },
  { key: "boxMeaning", label: "What each text box means", icon: <LayoutList className="h-3.5 w-3.5" /> },
  { key: "whyFunny", label: "Why it's funny", icon: <Smile className="h-3.5 w-3.5" /> },
  { key: "generality", label: "Generic vs. specific", icon: <Globe className="h-3.5 w-3.5" /> },
];

function LibraryTemplateCard({ template: t, isMaster, canCurate }: { template: LibraryTemplate; isMaster: boolean; canCurate: boolean }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});

  const startEditing = () => {
    setDraft(Object.fromEntries(CONTEXT_FIELDS.map((f) => [f.key as string, (t[f.key] as string | null) || ""])));
    setEditing(true);
  };

  const saveContext = useMutation({
    mutationFn: () => apiRequest("PATCH", `/api/meme-generator/template-library/${t.templateId}`, draft),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/meme-generator/template-library"] });
      queryClient.invalidateQueries({ queryKey: ["/api/meme-generator/templates"] });
      setEditing(false);
      toast({ title: "Context saved", description: `"${t.name}" now uses your edited context for new meme runs.` });
    },
    onError: (e: any) => toast({ title: "Could not save", description: e?.message, variant: "destructive" }),
  });

  const draftValid = CONTEXT_FIELDS.every((f) => (draft[f.key as string] || "").trim().length > 0);

  const deleteTemplate = useMutation({
    mutationFn: () => apiRequest("DELETE", `/api/meme-generator/template-library/${t.templateId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/meme-generator/template-library"] });
      queryClient.invalidateQueries({ queryKey: ["/api/meme-generator/templates"] });
      toast({ title: "Template deleted", description: `"${t.name}" is hidden and won't be re-imported by the daily refresh. Import it again via "Search Imgflip" to restore it.` });
    },
    onError: (e: any) => toast({ title: "Could not delete", description: e?.message, variant: "destructive" }),
  });

  // Curator-only: add/remove this already-in-library template from the shared
  // Recommended list. Additive — removing never deletes the underlying row.
  const setRecommended = useMutation({
    mutationFn: (recommended: boolean) =>
      recommended
        ? apiRequest("POST", "/api/meme-generator/template-library/recommended", { templateId: t.templateId })
        : apiRequest("DELETE", `/api/meme-generator/template-library/recommended/${t.templateId}`),
    onSuccess: (_res, recommended) => {
      queryClient.invalidateQueries({ queryKey: ["/api/meme-generator/template-library"] });
      queryClient.invalidateQueries({ queryKey: ["/api/meme-generator/templates"] });
      toast({
        title: recommended ? "Added to Recommended" : "Removed from Recommended",
        description: recommended
          ? `"${t.name}" now appears in the Recommended tab for everyone.`
          : `"${t.name}" is no longer recommended (the template itself is untouched).`,
      });
    },
    onError: (e: any) => toast({ title: "Could not update Recommended", description: e?.message, variant: "destructive" }),
  });

  return (
    <div className="rounded-xl border border-border/60 bg-background/60 overflow-hidden" data-testid={`card-library-template-${t.templateId}`}>
      <button
        type="button"
        className="w-full flex items-center gap-3 p-3 text-left hover:bg-muted/40 transition-colors"
        onClick={() => setOpen((v) => !v)}
        data-testid={`button-expand-template-${t.templateId}`}
      >
        <img src={t.url} alt={t.name} className="h-12 w-12 rounded-lg object-cover border border-border/40 shrink-0" loading="lazy" />
        <div className="flex flex-col min-w-0 flex-1">
          <span className="text-sm font-semibold truncate" data-testid={`text-library-name-${t.templateId}`}>{t.name}</span>
          <span className="text-[11px] text-muted-foreground">
            {t.boxCount ? `${t.boxCount} text box${t.boxCount === 1 ? "" : "es"}` : "Imgflip template"}
          </span>
        </div>
        {t.recommended && (
          <Badge variant="secondary" className="shrink-0 text-[10px] gap-1" data-testid={`badge-recommended-${t.templateId}`}>
            <Star className="h-2.5 w-2.5 fill-current" /> Recommended
          </Badge>
        )}
        {t.enriched ? (
          <Badge variant="default" className="shrink-0 text-[10px]">Enriched</Badge>
        ) : t.imageDeadAt ? (
          <Badge variant="destructive" className="shrink-0 text-[10px]">Image unavailable</Badge>
        ) : (
          <Badge variant="outline" className="shrink-0 text-[10px]">Not enriched</Badge>
        )}
        <ChevronDown className={cn("h-4 w-4 text-muted-foreground shrink-0 transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="border-t border-border/60 p-3 space-y-3 bg-muted/20" data-testid={`section-context-${t.templateId}`}>
          {t.enriched ? (
            <>
              {editing ? (
                <>
                  {CONTEXT_FIELDS.map((f) => (
                    <div key={f.key} className="space-y-1">
                      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground/80">
                        {f.icon} {f.label}
                      </div>
                      <Textarea
                        value={draft[f.key as string] || ""}
                        onChange={(e) => setDraft((d) => ({ ...d, [f.key as string]: e.target.value }))}
                        className="text-xs min-h-[64px]"
                        maxLength={4000}
                        disabled={saveContext.isPending}
                        data-testid={`input-context-${f.key}-${t.templateId}`}
                      />
                    </div>
                  ))}
                  <div className="flex items-center gap-2 pt-1">
                    <Button
                      size="sm" className="h-7 text-xs"
                      onClick={() => saveContext.mutate()}
                      disabled={saveContext.isPending || !draftValid}
                      data-testid={`button-save-context-${t.templateId}`}
                    >
                      {saveContext.isPending ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Check className="h-3 w-3 mr-1" />}
                      Save
                    </Button>
                    <Button
                      size="sm" variant="ghost" className="h-7 text-xs"
                      onClick={() => setEditing(false)}
                      disabled={saveContext.isPending}
                      data-testid={`button-cancel-context-${t.templateId}`}
                    >
                      <X className="h-3 w-3 mr-1" /> Cancel
                    </Button>
                    {!draftValid && (
                      <span className="text-[11px] text-muted-foreground">All five fields are required.</span>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {CONTEXT_FIELDS.map((f) => (
                    <div key={f.key} className="space-y-0.5">
                      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground/80">
                        {f.icon} {f.label}
                      </div>
                      <p className="text-xs leading-relaxed text-foreground/90" data-testid={`text-context-${f.key}-${t.templateId}`}>
                        {(t[f.key] as string | null) || "—"}
                      </p>
                    </div>
                  ))}
                  {isMaster && (
                    <Button
                      size="sm" variant="outline" className="h-7 text-xs"
                      onClick={startEditing}
                      data-testid={`button-edit-context-${t.templateId}`}
                    >
                      <Pencil className="h-3 w-3 mr-1" /> Edit context
                    </Button>
                  )}
                </>
              )}
            </>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                {t.imageDeadAt
                  ? "This template's image no longer exists on Imgflip, so it can't be enriched. It is skipped by automatic passes (re-checked periodically); a manual \"Enrich templates\" run re-checks it immediately."
                  : "No context yet — this template hasn't been enriched. It becomes selectable in the generator once enrichment runs."}
              </p>
              {t.lastEnrichError && (
                <p className="text-xs text-destructive flex items-start gap-1.5" data-testid={`text-enrich-error-${t.templateId}`}>
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  <span>
                    Last enrichment attempt failed{t.lastEnrichErrorAt ? ` (${new Date(t.lastEnrichErrorAt).toLocaleString()})` : ""}: {t.lastEnrichError}
                  </span>
                </p>
              )}
            </div>
          )}
          {!editing && (canCurate || isMaster) && (
            <div className="pt-1 border-t border-border/40 flex flex-wrap items-center gap-2">
              {canCurate && (
                <Button
                  size="sm" variant="outline" className="h-7 text-xs"
                  onClick={() => setRecommended.mutate(!t.recommended)}
                  disabled={setRecommended.isPending}
                  data-testid={`button-toggle-recommended-${t.templateId}`}
                >
                  {setRecommended.isPending ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Star className={cn("h-3 w-3 mr-1", t.recommended && "fill-current")} />}
                  {t.recommended ? "Remove from Recommended" : "Add to Recommended"}
                </Button>
              )}
              {isMaster && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    size="sm" variant="ghost"
                    className="h-7 text-xs text-destructive hover:text-destructive"
                    disabled={deleteTemplate.isPending}
                    data-testid={`button-delete-template-${t.templateId}`}
                  >
                    {deleteTemplate.isPending ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Trash2 className="h-3 w-3 mr-1" />}
                    Delete template
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete "{t.name}"?</AlertDialogTitle>
                    <AlertDialogDescription>
                      The template is hidden from the picker and this library, and the daily
                      Imgflip refresh will not re-import it. Existing meme runs and results are
                      not affected. You can restore it later by importing it again via
                      "Search Imgflip".
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid={`button-cancel-delete-${t.templateId}`}>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      onClick={() => deleteTemplate.mutate()}
                      data-testid={`button-confirm-delete-${t.templateId}`}
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Short human summary of a persisted automation run result. Manual runs write
// the same last-run record, so the trigger is always shown — "ran (automatic)"
// vs "ran (manual)" — to keep the daily-automation panel honest.
function automationRunLabel(kind: "refresh" | "enrich", info: AutomationRunInfo | undefined): string {
  if (!info?.lastAt) return "hasn't run yet";
  const when = new Date(info.lastAt).toLocaleString();
  const r = info.lastResult || {};
  const via = r.trigger === "auto" ? " (automatic)" : r.trigger === "manual" ? (r.mode === "all" ? " (manual, re-enrich all)" : " (manual)") : "";
  if (r.ok === false) return `failed${via} ${when}${r.error ? ` — ${String(r.error).slice(0, 120)}` : ""}`;
  if (kind === "refresh") {
    return `ran${via} ${when}${typeof r.imported === "number" ? ` — ${r.imported} templates imported` : ""}`;
  }
  if (r.mode === "all") {
    return `ran${via} ${when}${typeof r.enriched === "number" ? ` — ${r.enriched} re-enriched${r.failed ? `, ${r.failed} failed` : ""}` : ""}`;
  }
  return `ran${via} ${when}${typeof r.enriched === "number" ? ` — ${r.enriched} newly enriched, ${r.skipped ?? 0} already done${r.failed ? `, ${r.failed} failed` : ""}${typeof r.remaining === "number" && r.remaining > 0 ? `, ${r.remaining} remaining for later passes` : ""}` : ""}`;
}

// ── Recommended-tab curation search (curator only) ───────────────────────────
// Search Imgflip's full catalog and add a result straight to the shared, global
// "Recommended" list. Mirrors the picker's Search Imgflip flow, but the action
// is "Add" (import + enrich + mark recommended in one curator-gated server
// call). Only rendered for the curator; the server re-enforces on every add.
type RecoSearchResult = {
  id: string;
  name: string;
  url: string;
  status: "importable" | "analyzing" | "available" | "failed" | "pending";
  enrichError: string | null;
};

function RecommendedSearch({ recommendedIds }: { recommendedIds: Set<string> }) {
  const { toast } = useToast();
  const [input, setInput] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  useEffect(() => {
    const t = setTimeout(() => { setQ(input.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [input]);
  const enabled = q.length >= 2;
  const url = `/api/meme-generator/imgflip-search?q=${encodeURIComponent(q)}&page=${page}`;
  const { data, isLoading, isError, isFetching } = useQuery<{ results: RecoSearchResult[]; hasMore: boolean; page: number }>({
    queryKey: [url],
    enabled,
    staleTime: 0,
    // Poll while any visible result is analyzing so its state settles without a
    // manual refresh (Add kicks enrichment for brand-new templates).
    refetchInterval: (query) => {
      const d = query.state.data as { results?: RecoSearchResult[] } | undefined;
      return d?.results?.some((r) => r.status === "analyzing") ? 3000 : false;
    },
  });
  const results = data?.results ?? [];
  const [addingIds, setAddingIds] = useState<Set<string>>(new Set());
  const addToRecommended = useMutation({
    mutationFn: async (templateId: string) => {
      const res = await apiRequest("POST", "/api/meme-generator/template-library/recommended", { templateId, query: q, page });
      return res.json();
    },
    onMutate: (templateId: string) => setAddingIds((p) => new Set(p).add(templateId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/meme-generator/template-library"] });
      queryClient.invalidateQueries({ queryKey: ["/api/meme-generator/templates"] });
      queryClient.invalidateQueries({ queryKey: [url] });
      toast({ title: "Added to Recommended", description: "It appears for everyone once analysis finishes." });
    },
    onError: (e: any) => toast({ title: "Could not add", description: e?.message, variant: "destructive" }),
    onSettled: (_d, _e, templateId: string) => setAddingIds((p) => { const n = new Set(p); n.delete(templateId); return n; }),
  });

  return (
    <div className="rounded-xl border border-border/60 bg-muted/20 p-3 space-y-3" data-testid="section-recommended-search">
      <div className="relative max-w-sm">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Search Imgflip to add…" className="h-8 pl-8 text-xs" data-testid="input-recommended-search" />
      </div>
      {!enabled ? (
        <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="text-recommended-search-hint">
          <Search className="h-3.5 w-3.5 shrink-0" /> Type at least 2 characters to search Imgflip's full catalog, then Add a template to Recommended.
        </p>
      ) : isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="aspect-square rounded-xl" />)}
        </div>
      ) : isError ? (
        <p className="text-xs text-destructive flex items-center gap-1.5" data-testid="text-recommended-search-error"><AlertTriangle className="h-3.5 w-3.5 shrink-0" /> Imgflip search failed. Try again shortly.</p>
      ) : results.length === 0 ? (
        <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="text-recommended-search-empty"><ImageIcon className="h-3.5 w-3.5 shrink-0" /> No Imgflip templates match "{q}".</p>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 max-h-[360px] overflow-y-auto pr-1">
            {results.map((r) => {
              const already = recommendedIds.has(r.id);
              const busy = addingIds.has(r.id) || r.status === "analyzing";
              return (
                <div key={r.id} className="rounded-xl border border-border/60 overflow-hidden" data-testid={`card-reco-search-${r.id}`}>
                  <div className="aspect-square bg-muted/40">
                    <img src={r.url} alt={r.name} className="h-full w-full object-cover" loading="lazy" />
                  </div>
                  <div className="p-1.5 space-y-1.5">
                    <span className="block text-[11px] font-medium leading-tight line-clamp-2" data-testid={`text-reco-search-name-${r.id}`}>{r.name}</span>
                    {already ? (
                      <Badge variant="secondary" className="text-[10px] gap-1 w-full justify-center" data-testid={`badge-reco-added-${r.id}`}><Check className="h-2.5 w-2.5" /> Added</Badge>
                    ) : (
                      <Button
                        size="sm" variant="outline" className="h-6 w-full text-[10px]"
                        disabled={busy}
                        onClick={() => addToRecommended.mutate(r.id)}
                        data-testid={`button-add-recommended-${r.id}`}
                      >
                        {busy ? <Loader2 className="h-2.5 w-2.5 mr-1 animate-spin" /> : <Star className="h-2.5 w-2.5 mr-1" />}
                        {busy ? "Analyzing…" : "Add"}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {(data?.hasMore || page > 1) && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="h-7 text-xs" disabled={page <= 1 || isFetching} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="button-reco-search-prev">Previous</Button>
              <span className="text-xs text-muted-foreground" data-testid="text-reco-search-page">Page {page}</span>
              <Button variant="outline" size="sm" className="h-7 text-xs" disabled={!data?.hasMore || isFetching} onClick={() => setPage((p) => p + 1)} data-testid="button-reco-search-next">Next</Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function MemeSettingsSection({ isMaster }: { isMaster: boolean }) {
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [librarySort, setLibrarySort] = useState<"all" | "recommended" | "imported" | SortTabKey>("all");

  const { data: libraryData, isLoading } = useQuery<{ templates: LibraryTemplate[]; canCurateRecommended?: boolean }>({
    queryKey: ["/api/meme-generator/template-library"],
  });
  const library = libraryData?.templates ?? [];
  // Whether THIS account is the single designated curator (may add/remove from
  // Recommended). Server-provided and server-enforced — non-curators see the
  // list + Recommended flags but no curation controls.
  const canCurate = !!libraryData?.canCurateRecommended;
  const recommendedIds = useMemo(() => new Set(library.filter((t) => t.recommended).map((t) => t.templateId)), [library]);

  const { data: seedStatus } = useQuery<SeedStatus>({
    queryKey: ["/api/meme-generator/template-library/seed-status"],
    enabled: isMaster,
    refetchInterval: (query) => ((query.state.data as SeedStatus | undefined)?.running ? 3000 : false),
  });
  const seeding = !!seedStatus?.running;

  const startSeed = useMutation({
    mutationFn: (mode: "new" | "all") => apiRequest("POST", "/api/meme-generator/template-library/seed", { mode }),
    onSuccess: (_res, mode) => {
      queryClient.invalidateQueries({ queryKey: ["/api/meme-generator/template-library/seed-status"] });
      toast({
        title: mode === "all" ? "Re-enrichment started" : "Enrichment started",
        description:
          mode === "all"
            ? "Every enriched template is being re-analyzed in the background. Stored context (including manual edits) will be replaced as each finishes."
            : "A small batch of templates is being analyzed in the background. Already-enriched templates are skipped — click again later to continue the backlog.",
      });
    },
    onError: (e: any) => toast({ title: "Could not start", description: e?.message, variant: "destructive" }),
  });

  // When a seed pass transitions running→finished, pull the fresh library +
  // picker list (also refetch the library periodically WHILE running so newly
  // enriched templates stream in).
  const running = seedStatus?.running;
  const wasRunningRef = useRef(false);
  useEffect(() => {
    if (running) {
      wasRunningRef.current = true;
      const t = setInterval(() => {
        queryClient.invalidateQueries({ queryKey: ["/api/meme-generator/template-library"] });
      }, 5000);
      return () => clearInterval(t);
    }
    if (wasRunningRef.current) {
      wasRunningRef.current = false;
      queryClient.invalidateQueries({ queryKey: ["/api/meme-generator/template-library"] });
      queryClient.invalidateQueries({ queryKey: ["/api/meme-generator/templates"] });
    }
  }, [running]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = library;
    if (librarySort === "recommended") {
      rows = rows.filter((t) => t.recommended);
    } else if (librarySort === "imported") {
      // Rank-less templates — brought in on demand via the picker's Search
      // Imgflip tab (or dropped out of every Imgflip sort).
      rows = rows.filter(
        (t) => (t.rankTopNew ?? null) === null && (t.rankTop30 ?? null) === null && (t.rankTopAll ?? null) === null,
      );
    } else if (librarySort !== "all") {
      const rankKey = SORT_TABS.find((s) => s.key === librarySort)!.rankKey;
      rows = rows
        .filter((t) => rankOf(t, rankKey) !== null)
        .sort((a, b) => (rankOf(a, rankKey)! - rankOf(b, rankKey)!));
    }
    if (!q) return rows;
    return rows.filter((t) => t.name.toLowerCase().includes(q));
  }, [library, search, librarySort]);
  const enrichedCount = library.filter((t) => t.enriched).length;
  const unenrichedCount = library.length - enrichedCount;

  // Manual "refresh templates now" (masters). Blocking on the server (~15s of
  // Imgflip scraping) — the button spins until the import finishes.
  const refreshLibrary = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/meme-generator/template-library/refresh");
      return res.json();
    },
    onSuccess: (r: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/meme-generator/template-library"] });
      queryClient.invalidateQueries({ queryKey: ["/api/meme-generator/templates"] });
      queryClient.invalidateQueries({ queryKey: ["/api/meme-generator/template-library/seed-status"] });
      toast({
        title: "Templates refreshed",
        description: `${r?.imported ?? 0} templates imported from Imgflip's Top New / Top 30 Days / Top All Time lists.`,
      });
    },
    onError: (e: any) => toast({ title: "Refresh failed", description: e?.message, variant: "destructive" }),
  });

  return (
    <Card>
      <CardContent className="p-4 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <Settings2 className="h-4 w-4 text-primary" /> Meme template library
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5" data-testid="text-library-summary">
              {library.length
                ? `${enrichedCount} of ${library.length} templates enriched. Enriched templates are selectable in the generator, and their context is attached to every AI call.`
                : "No templates stored yet. Enrichment analyzes each Imgflip template once and stores how it works."}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative w-48">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search library…" className="h-8 pl-8 text-xs" data-testid="input-library-search" />
            </div>
            {isMaster && (
              <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => refreshLibrary.mutate()} disabled={refreshLibrary.isPending} data-testid="button-refresh-library">
                {refreshLibrary.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                {refreshLibrary.isPending ? "Importing…" : "Refresh templates"}
              </Button>
            )}
            {isMaster && (
              <Button
                size="sm" className="h-8 text-xs"
                onClick={() => {
                  if (library.length > 0 && unenrichedCount === 0) {
                    toast({ title: "Nothing to enrich", description: "Every template in the library is already enriched. Use \"Refresh templates\" to import new ones, or \"Re-enrich all\" to force a fresh pass." });
                    return;
                  }
                  startSeed.mutate("new");
                }}
                disabled={seeding || startSeed.isPending}
                data-testid="button-seed-library"
              >
                {seeding || startSeed.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
                {seeding ? "Enriching…" : library.length > 0 ? `Enrich new templates (${unenrichedCount})` : "Enrich new templates"}
              </Button>
            )}
            {isMaster && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    size="sm" variant="outline" className="h-8 text-xs"
                    disabled={seeding || startSeed.isPending || enrichedCount === 0}
                    data-testid="button-reenrich-all"
                  >
                    <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Re-enrich all
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Re-enrich all {enrichedCount} enriched templates?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This forces a fresh AI enrichment pass over every currently-enriched template. Each template is re-billed
                      (one AI call per template), and the stored context — including any manual edits you've made — will be
                      overwritten with the fresh results. Templates that haven't been enriched yet are not touched.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="button-cancel-reenrich">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => startSeed.mutate("all")} data-testid="button-confirm-reenrich">
                      Re-enrich all {enrichedCount} templates
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>

        {/* Sort tabs: All + the three Imgflip sorts (rank-ordered) + Imported */}
        <div className="flex items-center gap-1 rounded-lg bg-muted/60 p-1 w-fit" data-testid="tabs-library-sort">
          {([{ key: "all" as const, label: "All" }, { key: "recommended" as const, label: "Recommended" }, ...SORT_TABS, { key: "imported" as const, label: "Imported" }] as Array<{ key: "all" | "recommended" | "imported" | SortTabKey; label: string }>).map((s) => (
            <button
              key={s.key}
              type="button"
              className={cn(
                "rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
                librarySort === s.key ? "bg-background shadow-sm" : "text-muted-foreground",
              )}
              onClick={() => setLibrarySort(s.key)}
              data-testid={`button-library-sort-${s.key}`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Master-only: daily automation status (persisted server-side) */}
        {isMaster && seedStatus?.automation && (
          <div className="rounded-lg border border-border/60 bg-muted/30 p-3 text-xs text-muted-foreground space-y-1" data-testid="text-automation-status">
            <div className="flex items-center gap-1.5 font-semibold uppercase tracking-[0.06em] text-muted-foreground/80">
              <RefreshCw className="h-3 w-3" /> Daily automation
            </div>
            <p data-testid="text-automation-refresh">
              Template import (daily): {automationRunLabel("refresh", seedStatus.automation.refresh)}
            </p>
            <p data-testid="text-automation-enrich">
              Auto-enrichment (daily, 5 AM ET): {automationRunLabel("enrich", seedStatus.automation.enrich)}
            </p>
            {seedStatus.imgflipPremium && (
              <p data-testid="text-imgflip-premium-status">
                Imgflip Premium search: {premiumSearchLabel(seedStatus.imgflipPremium)}
              </p>
            )}
          </div>
        )}

        {isMaster && seedStatus && (seedStatus.running || seedStatus.error || (seedStatus.finishedAt && (seedStatus.remaining ?? 0) > 0)) && (
          <div className={cn("rounded-lg border p-3 text-xs flex items-center gap-2", seedStatus.error ? "border-destructive/40 bg-destructive/5 text-destructive" : "border-border/60 bg-muted/30 text-muted-foreground")} data-testid="text-seed-status">
            {seedStatus.running ? <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" /> : seedStatus.error ? <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> : <Sparkles className="h-3.5 w-3.5 shrink-0" />}
            {seedStatus.error
              ? `Enrichment failed: ${seedStatus.error}`
              : seedStatus.running
                ? seedStatus.mode === "all"
                  ? `Re-enriching ALL enriched templates (${seedStatus.batchSize ?? 0} in this pass)… ${seedStatus.enriched} re-enriched so far${seedStatus.failed ? `, ${seedStatus.failed} failed` : ""}${seedStatus.deadSkipped ? `, ${seedStatus.deadSkipped} skipped (dead image)` : ""}.`
                  : `Enriching new templates (up to ${seedStatus.batchSize ?? 10} per pass)… ${seedStatus.enriched} newly enriched, ${seedStatus.skipped} already done${seedStatus.failed ? `, ${seedStatus.failed} failed` : ""}${seedStatus.deadSkipped ? `, ${seedStatus.deadSkipped} skipped (dead image)` : ""}.`
                : seedStatus.mode === "all"
                  ? `Last re-enrich-all pass: ${seedStatus.enriched} re-enriched${seedStatus.failed ? `, ${seedStatus.failed} failed (previous context kept)` : ""}${seedStatus.deadSkipped ? `, ${seedStatus.deadSkipped} skipped (dead image)` : ""}.`
                  : `Last batch: ${seedStatus.enriched} newly enriched, ${seedStatus.skipped} already done${seedStatus.failed ? `, ${seedStatus.failed} failed` : ""}${typeof seedStatus.remaining === "number" && seedStatus.remaining > 0 ? ` — ${seedStatus.remaining} remaining for later passes` : ""}.`}
          </div>
        )}

        {librarySort === "recommended" && canCurate && (
          <RecommendedSearch recommendedIds={recommendedIds} />
        )}
        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-[70px] rounded-xl" />)}
          </div>
        ) : librarySort === "recommended" ? (
          filtered.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border/70 p-8 text-center text-sm text-muted-foreground" data-testid="text-recommended-empty">
              No recommended templates yet.{canCurate ? " Search Imgflip above and add the ones you want everyone to see." : " The curator hasn't added any yet."}
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {filtered.map((t) => <LibraryTemplateCard key={t.templateId} template={t} isMaster={isMaster} canCurate={canCurate} />)}
            </div>
          )
        ) : library.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border/70 p-8 text-center text-sm text-muted-foreground" data-testid="text-library-empty">
            The template library is empty.{isMaster ? " Click \"Enrich templates\" to fetch the Imgflip catalog and generate context for each template." : " A master user needs to run enrichment first."}
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-lg border border-border/60 bg-muted/30 p-4 text-sm text-muted-foreground flex items-center gap-2">
            <ImageIcon className="h-4 w-4 shrink-0" /> No templates match your search.
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {filtered.map((t) => <LibraryTemplateCard key={t.templateId} template={t} isMaster={isMaster} canCurate={canCurate} />)}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
